/**
 * WorldScene.ts - Overworld exploration with tile-based rendering
 * Handles player movement, encounters, and world navigation
 * Now with FF1-style sprite graphics!
 */

import { Scene, GameInput, GameKey, BattleEnemy } from './types/engine';
import { Renderer } from './Renderer';
import { TileMap } from './TileMap';
import { SpriteManager } from './SpriteManager';
import { CharacterSprite, Direction } from './CharacterSprite';
import {
  TerrainType,
  TERRAIN_COLORS,
  WORLD_TILESET,
  getTileVariant,
  getAnimatedTileFrame,
  isTerrainWalkable,
  getEncounterRate,
} from './data/TilesetConfig';
import {
  WORLD_MAP,
  WORLD_MAP_WIDTH,
  WORLD_MAP_HEIGHT,
  LOCATIONS as WORLD_LOCATIONS,
  Location as WorldLocation,
  LocationType,
} from './data/WorldMapData';
import { WorldEffects } from './effects/WorldEffects';
import { EncounterSystem } from './EncounterSystem';
import { NPCManager } from './NPCManager';
import { Location as StoryLocation } from './data/StoryData';

interface PlayerState {
  x: number;
  y: number;
  tileX: number;
  tileY: number;
  facing: 'up' | 'down' | 'left' | 'right';
  moving: boolean;
  moveProgress: number;
  animFrame: number;
  animTimer: number;
}

interface Location {
  x: number;
  y: number;
  name: string;
  type: 'town' | 'castle' | 'dungeon' | 'cave' | 'shrine';
}

export class WorldScene implements Scene {
  private renderer: Renderer;
  private tileMap: TileMap;
  private spriteManager: SpriteManager;

  // Player state
  private player: PlayerState;
  private characterSprite: CharacterSprite;
  private readonly TILE_SIZE = 16;
  private readonly MOVE_SPEED = 4; // pixels per frame
  private readonly ANIM_SPEED = 150; // ms per frame

  // World data - now using authored map from WorldMapData.ts
  private worldWidth: number = WORLD_MAP_WIDTH;
  private worldHeight: number = WORLD_MAP_HEIGHT;
  private worldTiles: TerrainType[][] = [];
  private locations: Location[] = [];

  // Encounter system
  private encounterSystem: EncounterSystem;
  private encounterCallback: ((enemies: BattleEnemy[], zone: string) => void) | null = null;

  // Location entry callback
  private onEnterLocationCallback: ((location: Location) => void) | null = null;

  // NPC system
  private npcManager: NPCManager;
  private currentStoryLocation: StoryLocation = StoryLocation.CORNELIA;

  // Input cooldown (prevent double-tap)
  private inputCooldown: number = 0;
  private readonly INPUT_COOLDOWN_MS = 100;

  // Animation timing
  private globalTime: number = 0;

  // Visual effects
  private worldEffects: WorldEffects;

  // Debug flag
  private _debugLogged: boolean = false;

  // Track if scene has been initialized (to preserve position on return from battle)
  private _initialized: boolean = false;

  constructor(
    ctx: CanvasRenderingContext2D,
    spriteManager: SpriteManager
  ) {
    this.renderer = new Renderer(ctx);
    this.spriteManager = spriteManager;
    this.tileMap = new TileMap(spriteManager, 'world_tileset', this.TILE_SIZE);

    this.player = {
      x: 0,
      y: 0,
      tileX: 32,
      tileY: 32,
      facing: 'down',
      moving: false,
      moveProgress: 0,
      animFrame: 0,
      animTimer: 0,
    };

    // Create character sprite (default to Fighter class)
    this.characterSprite = new CharacterSprite(spriteManager, {
      characterClass: 'FIGHTER',
      x: this.player.tileX * this.TILE_SIZE,
      y: this.player.tileY * this.TILE_SIZE,
      tileSize: this.TILE_SIZE,
    });

    // Initialize encounter system
    this.encounterSystem = new EncounterSystem();

    // Initialize NPC manager
    this.npcManager = new NPCManager(spriteManager, this.currentStoryLocation);

    // Generate initial world
    this.generateWorld();

    // Tell tileMap about world dimensions so viewport clamping works
    this.tileMap.width = this.worldWidth;
    this.tileMap.height = this.worldHeight;

    // Initialize visual effects (256x240 is NES resolution)
    this.worldEffects = new WorldEffects(256, 240);
  }

  // =========================================================================
  // Scene Interface
  // =========================================================================

  init(): void {
    // Initialize procedural sprites for immediate use
    this.spriteManager.initializeWithProceduralSprites();

    // Only reset player position on first initialization
    // This preserves position when returning from battle
    if (!this._initialized) {
      // Set player near Cornelia Town (starting location from WorldMapData)
      // Cornelia Town is at (12, 10), we start just south of it
      this.player.tileX = 12;
      this.player.tileY = 11;
      this.updatePlayerPixelPosition();
      this._initialized = true;
      console.log('[WorldScene] FIRST INIT - Player set near Cornelia:', this.player.tileX, this.player.tileY);
    } else {
      // Returning from battle or another scene - keep position
      console.log('[WorldScene] RETURNING - Preserving position:', this.player.tileX, this.player.tileY);
    }

    // Update character sprite position
    this.characterSprite.setPosition(
      this.player.tileX * this.TILE_SIZE,
      this.player.tileY * this.TILE_SIZE
    );

    // Center camera on player
    this.tileMap.centerOn(this.player.x, this.player.y);

    // Reset debug flag and time
    this._debugLogged = false;
    this.globalTime = 0;
  }

  update(deltaTime: number): void {
    // Update global time for animations
    this.globalTime += deltaTime;

    // Update input cooldown (but not if dialogue is active)
    if (!this.npcManager.isDialogueActive() && this.inputCooldown > 0) {
      this.inputCooldown -= deltaTime;
    }

    // Update movement animation (pause during dialogue)
    if (!this.npcManager.isDialogueActive() && this.player.moving) {
      this.updateMovement(deltaTime);
    }

    // Update walk animation
    if (!this.npcManager.isDialogueActive()) {
      this.player.animTimer += deltaTime;
      if (this.player.animTimer >= this.ANIM_SPEED) {
        this.player.animTimer = 0;
        this.player.animFrame = (this.player.animFrame + 1) % 4;
      }
    }

    // Update character sprite
    this.characterSprite.update(deltaTime);

    // Update NPC manager (always update for dialogue animations)
    this.npcManager.update(deltaTime);

    // Update visual effects
    this.worldEffects.update(deltaTime);
  }

  render(ctx: CanvasRenderingContext2D): void {
    this.renderer.setContext(ctx);

    // Clear screen with black (FF1 style)
    this.renderer.clear('#000000');

    // Render tilemap
    this.renderWorld(ctx);

    // Render locations with glow effects
    this.renderLocations(ctx);

    // Render NPCs (behind player)
    const viewport = this.tileMap.viewport;
    this.npcManager.render(this.renderer, viewport.x, viewport.y, viewport.width, viewport.height);

    // Render player
    this.renderPlayer(ctx);

    // Render world effects (particles, clouds, day/night)
    this.worldEffects.render(ctx);

    // Render encounter warning if close to battle (unless dialogue active)
    if (!this.npcManager.isDialogueActive()) {
      const stepInfo = this.encounterSystem.getStepCount();
      const dangerLevel = stepInfo.current / stepInfo.target;
      this.worldEffects.renderEncounterWarning(ctx, dangerLevel);
    }

    // Debug info (only in development)
    // if (process.env.NODE_ENV === 'production') {
    //   this.renderDebugInfo(ctx);
    // }
  }

  handleInput(input: GameInput): void {
    // Debug: log input state
    if (input.pressed.size > 0) {
      console.log(`[WorldScene] handleInput called, pressed keys:`, Array.from(input.pressed));
    }

    // Let NPC manager handle input first (for dialogue)
    if (this.npcManager.handleInput(input)) {
      return; // Dialogue handled the input
    }

    // Don't process input while moving or on cooldown or during dialogue
    if (this.player.moving || this.inputCooldown > 0 || this.npcManager.isDialogueActive()) {
      return;
    }

    // Check for menu button - let parent handle menu opening
    if (input.justPressed.has(GameKey.START)) {
      // Menu opening is handled by GameEngine through keyboard shortcuts
      // or by the Game component button
      return;
    }

    // Action button (A) - interact with NPCs or locations
    if (input.justPressed.has(GameKey.A)) {
      // Try to interact with NPC first
      this.npcManager.tryInteract(
        this.player.tileX,
        this.player.tileY,
        this.player.facing as Direction
      );

      // If no NPC, try location interaction
      if (!this.npcManager.isDialogueActive()) {
        this.tryInteract();
      }
      return;
    }

    // Handle movement
    let dx = 0;
    let dy = 0;
    let newFacing: 'up' | 'down' | 'left' | 'right' | null = null;

    if (input.pressed.has(GameKey.UP)) {
      dy = -1;
      newFacing = 'up';
    } else if (input.pressed.has(GameKey.DOWN)) {
      dy = 1;
      newFacing = 'down';
    } else if (input.pressed.has(GameKey.LEFT)) {
      dx = -1;
      newFacing = 'left';
    } else if (input.pressed.has(GameKey.RIGHT)) {
      dx = 1;
      newFacing = 'right';
    }

    if (newFacing) {
      this.player.facing = newFacing;
    }

    if (dx !== 0 || dy !== 0) {
      this.tryMove(dx, dy);
    }
  }

  cleanup(): void {
    // Nothing to cleanup
  }

  // =========================================================================
  // Movement
  // =========================================================================

  private tryMove(dx: number, dy: number): void {
    const newTileX = this.player.tileX + dx;
    const newTileY = this.player.tileY + dy;

    // Check bounds
    if (newTileX < 0 || newTileX >= this.worldWidth || newTileY < 0 || newTileY >= this.worldHeight) {
      console.log(`[WorldScene] Move blocked - out of bounds: (${newTileX}, ${newTileY})`);
      return;
    }

    // Check if NPC is blocking
    if (this.npcManager.isBlocked(newTileX, newTileY)) {
      // Bump into NPC triggers dialogue
      this.npcManager.tryInteract(
        this.player.tileX,
        this.player.tileY,
        this.player.facing as Direction
      );
      console.log(`[WorldScene] Move blocked - NPC at: (${newTileX}, ${newTileY})`);
      return;
    }

    // Check if walkable
    const terrain = this.worldTiles[newTileY]?.[newTileX];
    if (terrain === undefined || !this.isWalkable(terrain)) {
      console.log(`[WorldScene] Move blocked - terrain ${terrain} at (${newTileX}, ${newTileY}) not walkable`);
      return;
    }

    console.log(`[WorldScene] Moving to (${newTileX}, ${newTileY}), terrain: ${terrain}`);

    // Determine direction for character sprite
    const direction: Direction = dx < 0 ? 'left' : dx > 0 ? 'right' : dy < 0 ? 'up' : 'down';

    // Start movement
    this.player.tileX = newTileX;
    this.player.tileY = newTileY;
    this.player.moving = true;
    this.player.moveProgress = 0;
    this.inputCooldown = this.INPUT_COOLDOWN_MS;

    // Update character sprite movement
    const targetX = newTileX * this.TILE_SIZE;
    const targetY = newTileY * this.TILE_SIZE;
    this.characterSprite.moveTo(targetX, targetY, direction);

    // Spawn footstep dust particles
    const screenPos = this.tileMap.worldToScreen(this.player.x, this.player.y);
    this.worldEffects.spawnFootstepDust(screenPos.x, screenPos.y);

    // Check for random encounter
    const shouldEncounter = this.encounterSystem.checkEncounter(terrain as any, newTileX, newTileY);
    if (shouldEncounter) {
      this.triggerEncounter(terrain, newTileX, newTileY);
    }
  }

  private updateMovement(_deltaTime: number): void {
    this.player.moveProgress += this.MOVE_SPEED;

    if (this.player.moveProgress >= this.TILE_SIZE) {
      // Movement complete
      this.player.moving = false;
      this.player.moveProgress = 0;
      this.updatePlayerPixelPosition();

      // Center camera on player
      this.tileMap.centerOn(this.player.x, this.player.y);
    } else {
      // Interpolate position
      this.updatePlayerPixelPosition();
    }
  }

  private updatePlayerPixelPosition(): void {
    this.player.x = this.player.tileX * this.TILE_SIZE + this.TILE_SIZE / 2;
    this.player.y = this.player.tileY * this.TILE_SIZE + this.TILE_SIZE / 2;
  }

  private isWalkable(terrain: TerrainType): boolean {
    // Use the tileset config for walkability check
    return isTerrainWalkable(terrain);
  }

  // =========================================================================
  // Rendering
  // =========================================================================

  private renderWorld(_ctx: CanvasRenderingContext2D): void {
    const viewport = this.tileMap.viewport;
    const startCol = Math.floor(viewport.x / this.TILE_SIZE);
    const endCol = Math.ceil((viewport.x + viewport.width) / this.TILE_SIZE);
    const startRow = Math.floor(viewport.y / this.TILE_SIZE);
    const endRow = Math.ceil((viewport.y + viewport.height) / this.TILE_SIZE);

    // Clamp to world bounds
    const clampedStartCol = Math.max(0, startCol);
    const clampedEndCol = Math.min(this.worldWidth, endCol);
    const clampedStartRow = Math.max(0, startRow);
    const clampedEndRow = Math.min(this.worldHeight, endRow);

    // Render tiles
    for (let row = clampedStartRow; row < clampedEndRow; row++) {
      for (let col = clampedStartCol; col < clampedEndCol; col++) {
        const terrain = this.worldTiles[row]?.[col];
        if (terrain === undefined) continue;

        const screenPos = this.tileMap.worldToScreen(
          col * this.TILE_SIZE,
          row * this.TILE_SIZE
        );

        // Get tile configuration
        const tileConfig = WORLD_TILESET[terrain];

        if (tileConfig) {
          // Determine which tile ID to use
          let tileId: number;

          if (tileConfig.frames && tileConfig.frames.length > 0) {
            // Animated tile (water, lava, etc.)
            tileId = getAnimatedTileFrame(terrain, this.globalTime);
          } else if (tileConfig.variants && tileConfig.variants.length > 0) {
            // Variant tile (different grass textures, etc.)
            tileId = getTileVariant(terrain, col, row);
          } else {
            // Base tile
            tileId = tileConfig.baseId;
          }

          // Try to get sprite from sprite manager
          const sprite = this.spriteManager.getSprite('world_tileset', tileId);

          if (sprite) {
            this.renderer.drawSprite(sprite, screenPos.x, screenPos.y);
          } else {
            // Fallback to colored rectangle
            const color = TERRAIN_COLORS[terrain] || '#ff00ff';
            this.renderer.drawRect(screenPos.x, screenPos.y, this.TILE_SIZE, this.TILE_SIZE, color, true);
          }

          // Spawn ambient particles based on terrain type
          if (terrain === TerrainType.OCEAN || terrain === TerrainType.RIVER) {
            this.worldEffects.spawnWaterSparkle(screenPos.x, screenPos.y);
          } else if (terrain === TerrainType.FOREST) {
            this.worldEffects.spawnLeaf(screenPos.x, screenPos.y);
          }
        } else {
          // Unknown terrain - draw magenta for debugging
          this.renderer.drawRect(screenPos.x, screenPos.y, this.TILE_SIZE, this.TILE_SIZE, '#ff00ff', true);
        }
      }
    }
  }

  private renderLocations(ctx: CanvasRenderingContext2D): void {
    this.locations.forEach((location) => {
      const screenPos = this.tileMap.worldToScreen(
        location.x * this.TILE_SIZE,
        location.y * this.TILE_SIZE
      );

      // Only render if on screen
      if (screenPos.x < -20 || screenPos.x > 280 || screenPos.y < -20 || screenPos.y > 260) {
        return;
      }

      // Render glow effect behind the location
      this.worldEffects.renderLocationGlow(ctx, screenPos.x, screenPos.y, location.type);

      // Draw location marker based on type - now with distinct icons
      this.renderLocationIcon(ctx, screenPos.x, screenPos.y, location.type);

      // Draw name if close to player
      const dx = Math.abs(location.x - this.player.tileX);
      const dy = Math.abs(location.y - this.player.tileY);
      if (dx <= 4 && dy <= 4) {
        this.renderer.drawText(location.name, screenPos.x + this.TILE_SIZE / 2, screenPos.y - 10, {
          font: 'ff1',
          fontSize: 8,
          color: '#ffffff',
          align: 'center',
          shadow: true,
        });
      }
    });
  }

  private renderLocationIcon(ctx: CanvasRenderingContext2D, x: number, y: number, type: string): void {
    ctx.save();

    switch (type) {
      case 'town':
        // Town: Orange house shape
        ctx.fillStyle = '#ffaa00';
        ctx.fillRect(x + 2, y + 8, 12, 8); // Building base
        ctx.fillStyle = '#cc8800';
        ctx.beginPath();
        ctx.moveTo(x, y + 8);
        ctx.lineTo(x + 8, y + 2);
        ctx.lineTo(x + 16, y + 8);
        ctx.closePath();
        ctx.fill(); // Roof
        ctx.fillStyle = '#442200';
        ctx.fillRect(x + 6, y + 10, 4, 6); // Door
        break;

      case 'castle':
        // Castle: White battlements
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(x + 2, y + 6, 12, 10); // Main body
        ctx.fillRect(x, y + 2, 4, 6); // Left tower
        ctx.fillRect(x + 12, y + 2, 4, 6); // Right tower
        ctx.fillStyle = '#aaaaaa';
        ctx.fillRect(x + 1, y + 2, 2, 2); // Left battlement
        ctx.fillRect(x + 13, y + 2, 2, 2); // Right battlement
        ctx.fillStyle = '#442200';
        ctx.fillRect(x + 6, y + 10, 4, 6); // Gate
        break;

      case 'dungeon':
        // Dungeon: Red skull entrance
        ctx.fillStyle = '#660000';
        ctx.fillRect(x + 2, y + 4, 12, 12); // Cave frame
        ctx.fillStyle = '#220000';
        ctx.beginPath();
        ctx.arc(x + 8, y + 12, 5, 0, Math.PI, true);
        ctx.fill(); // Cave opening
        ctx.fillStyle = '#ff0000';
        ctx.fillRect(x + 5, y + 4, 6, 4); // Warning banner
        break;

      case 'cave':
        // Cave: Dark brown entrance
        ctx.fillStyle = '#443333';
        ctx.fillRect(x + 2, y + 4, 12, 12);
        ctx.fillStyle = '#221111';
        ctx.beginPath();
        ctx.arc(x + 8, y + 12, 5, 0, Math.PI, true);
        ctx.fill();
        // Small rocks around entrance
        ctx.fillStyle = '#666666';
        ctx.fillRect(x + 1, y + 10, 3, 3);
        ctx.fillRect(x + 12, y + 11, 3, 2);
        break;

      case 'shrine':
        // Shrine: Blue mystical building
        ctx.fillStyle = '#4488ff';
        ctx.fillRect(x + 4, y + 6, 8, 10);
        ctx.fillStyle = '#88aaff';
        ctx.beginPath();
        ctx.moveTo(x + 2, y + 6);
        ctx.lineTo(x + 8, y + 1);
        ctx.lineTo(x + 14, y + 6);
        ctx.closePath();
        ctx.fill();
        // Glowing orb
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(x + 8, y + 4, 2, 0, Math.PI * 2);
        ctx.fill();
        break;

      default:
        // Fallback: Simple colored square
        ctx.fillStyle = '#ff00ff';
        ctx.fillRect(x, y, 16, 16);
    }

    ctx.restore();
  }

  private renderPlayer(_ctx: CanvasRenderingContext2D): void {
    const screenPos = this.tileMap.worldToScreen(this.player.x - 8, this.player.y - 8);

    // Debug: Log player position once
    if (!this._debugLogged) {
      console.log('[WorldScene] Player tile:', this.player.tileX, this.player.tileY);
      console.log('[WorldScene] Player pixel:', this.player.x, this.player.y);
      console.log('[WorldScene] Screen pos:', screenPos);
      console.log('[WorldScene] Viewport:', this.tileMap.viewport);
      this._debugLogged = true;
    }

    // Render character sprite with shadow
    this.characterSprite.renderWithShadow(this.renderer, screenPos.x, screenPos.y);
  }

  private renderDebugInfo(_ctx: CanvasRenderingContext2D): void {
    const terrainAtPlayer = this.worldTiles[this.player.tileY]?.[this.player.tileX];
    const stepInfo = this.encounterSystem.getStepCount();
    const info = [
      `Pos: (${this.player.tileX}, ${this.player.tileY})`,
      `Steps: ${stepInfo.current}/${stepInfo.target}`,
      `Terrain: ${terrainAtPlayer !== undefined ? TerrainType[terrainAtPlayer] : 'Unknown'}`,
    ];

    info.forEach((text, i) => {
      this.renderer.drawText(text, 2, 2 + i * 10, {
        font: 'ff1',
        fontSize: 8,
        color: '#00ff00',
        shadow: true,
      });
    });
  }

  // =========================================================================
  // World Generation
  // =========================================================================

  private generateWorld(): void {
    // Use the authored FF1-style world map from WorldMapData.ts
    // This gives us proper continents, locations, and geography
    this.worldTiles = WORLD_MAP.map(row => [...row]) as TerrainType[][];
    this.worldWidth = WORLD_MAP_WIDTH;
    this.worldHeight = WORLD_MAP_HEIGHT;

    // Convert WorldMapData locations to our local Location format
    this.locations = WORLD_LOCATIONS.map(loc => ({
      x: loc.x,
      y: loc.y,
      name: loc.name,
      type: this.convertLocationType(loc.type),
    }));

    console.log(`[WorldScene] Loaded authored world map: ${this.worldWidth}x${this.worldHeight} with ${this.locations.length} locations`);
  }

  private convertLocationType(type: LocationType): 'town' | 'castle' | 'dungeon' | 'cave' | 'shrine' {
    switch (type) {
      case LocationType.TOWN: return 'town';
      case LocationType.CASTLE: return 'castle';
      case LocationType.DUNGEON: return 'dungeon';
      case LocationType.CAVE: return 'cave';
      case LocationType.SHRINE: return 'shrine';
      case LocationType.SPECIAL: return 'dungeon';
      default: return 'town';
    }
  }

  // =========================================================================
  // Encounters
  // =========================================================================

  private triggerEncounter(_terrain: TerrainType, x: number, y: number): void {
    // Get the encounter zone for this location
    const zone = this.encounterSystem.getLastEncounterZone();
    if (!zone) {
      console.warn('[WorldScene] No encounter zone found at', x, y);
      return;
    }

    // Select enemy group from zone
    const enemyGroup = this.encounterSystem.selectEnemyGroup(zone);

    // Generate enemy instances for battle
    const enemies = this.encounterSystem.generateEnemies(enemyGroup);

    if (enemies.length === 0) {
      console.warn('[WorldScene] No enemies generated for encounter');
      return;
    }

    console.log(`[WorldScene] Encounter! Zone: ${zone}, Enemies:`, enemies.map(e => e.name).join(', '));

    // Flash screen white for encounter effect
    this.worldEffects.startFlash('#ffffff');

    // Trigger battle after a brief delay for the flash
    setTimeout(() => {
      if (this.encounterCallback) {
        this.encounterCallback(enemies, zone);
      }
    }, 100);
  }

  private tryInteract(): void {
    // Check if standing on a location
    const location = this.locations.find(
      (loc) => loc.x === this.player.tileX && loc.y === this.player.tileY
    );

    if (location) {
      console.log(`Entering ${location.name}`);
      // Trigger appropriate scene based on location type
      if (this.onEnterLocationCallback) {
        this.onEnterLocationCallback(location);
      }
    }
  }

  // =========================================================================
  // Public API
  // =========================================================================

  /**
   * Set player position (in tiles)
   * Clamps to world bounds and ensures player is on walkable terrain
   */
  setPosition(x: number, y: number): void {
    // Clamp to world bounds
    let tileX = Math.max(0, Math.min(x, this.worldWidth - 1));
    let tileY = Math.max(0, Math.min(y, this.worldHeight - 1));

    // Check if the position is walkable - if not, default to Cornelia (starting area)
    const terrain = this.worldTiles[tileY]?.[tileX];
    if (terrain === undefined || !this.isWalkable(terrain)) {
      console.log('[WorldScene] setPosition: position', tileX, tileY, 'is not walkable (terrain:', terrain, '), defaulting to Cornelia');
      // Cornelia Town is at (12, 10) in WorldMapData, start just south of it
      tileX = 12;
      tileY = 11;
    }

    this.player.tileX = tileX;
    this.player.tileY = tileY;

    console.log('[WorldScene] setPosition called with:', x, y, '-> final position:', this.player.tileX, this.player.tileY);

    this.updatePlayerPixelPosition();
    this.tileMap.centerOn(this.player.x, this.player.y);

    // Update character sprite position
    this.characterSprite.setPosition(
      this.player.tileX * this.TILE_SIZE,
      this.player.tileY * this.TILE_SIZE
    );
  }

  /**
   * Set the character class for the player sprite
   */
  setCharacterClass(characterClass: string): void {
    this.characterSprite.setCharacterClass(characterClass);
  }

  /**
   * Get player position
   */
  getPosition(): { x: number; y: number } {
    return {
      x: this.player.tileX,
      y: this.player.tileY,
    };
  }

  /**
   * Set encounter callback
   */
  onEncounter(callback: (enemies: BattleEnemy[], zone: string) => void): void {
    this.encounterCallback = callback;
  }

  /**
   * Force an encounter (for testing or scripted battles)
   */
  startEncounter(): void {
    const terrain = this.worldTiles[this.player.tileY]?.[this.player.tileX];
    if (terrain !== undefined) {
      // Force an encounter check
      const shouldEncounter = this.encounterSystem.checkEncounter(
        terrain as any,
        this.player.tileX,
        this.player.tileY,
        true // Force encounter
      );

      if (shouldEncounter) {
        this.triggerEncounter(terrain, this.player.tileX, this.player.tileY);
      }
    }
  }

  /**
   * Trigger a boss encounter at the player's location
   */
  startBossEncounter(bossId: number): void {
    const enemies = this.encounterSystem.generateBossEncounter(bossId);
    if (enemies.length === 0) {
      console.error('[WorldScene] Failed to generate boss encounter');
      return;
    }

    console.log(`[WorldScene] Boss Encounter! ${enemies[0].name}`);

    // Flash screen red for boss encounter
    this.worldEffects.startFlash('#ff0000');

    // Trigger battle after flash
    setTimeout(() => {
      if (this.encounterCallback) {
        this.encounterCallback(enemies, 'BOSS');
      }
    }, 150);
  }

  /**
   * Get the encounter system (for external access)
   */
  getEncounterSystem(): EncounterSystem {
    return this.encounterSystem;
  }

  /**
   * Set world map data (for loading from contract)
   */
  setWorldMap(tiles: number[][], locations: Location[]): void {
    this.worldTiles = tiles as TerrainType[][];
    this.worldHeight = tiles.length;
    this.worldWidth = tiles[0]?.length || 0;
    this.locations = locations;
  }

  /**
   * Get current terrain type
   */
  getCurrentTerrain(): string {
    const terrain = this.worldTiles[this.player.tileY]?.[this.player.tileX];
    return terrain !== undefined ? TerrainType[terrain] : 'Unknown';
  }

  /**
   * Get NPC manager (for external access)
   */
  getNPCManager(): NPCManager {
    return this.npcManager;
  }

  /**
   * Change location and load new NPCs
   */
  setLocation(location: StoryLocation): void {
    this.currentStoryLocation = location;
    this.npcManager.loadNPCsForLocation(location);
    console.log(`[WorldScene] Changed location to ${location}`);
  }

  /**
   * Check if dialogue is active
   */
  isDialogueActive(): boolean {
    return this.npcManager.isDialogueActive();
  }

  /**
   * Set callback for when player enters a location (town, castle, dungeon)
   */
  onEnterLocation(callback: (location: Location) => void): void {
    this.onEnterLocationCallback = callback;
  }

  /**
   * Get all locations on the map
   */
  getLocations(): Location[] {
    return this.locations;
  }
}
